import { IconButton, Tooltip, Stack, styled, Card, CircularProgress } from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import { useContext, useState, useMemo, useCallback } from "react";
import { context } from "utils/Provider";
import { useManualUnmatch } from "../../matcher/services/useManualUnmatch";
import { useManualMerge } from "../../matcher/services/useManualMerge";
import ManualUnmatch from "../../matcher/components/ManualUnmatchTable";
import EditIcon from "@mui/icons-material/Edit";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CustomTable from "../../matcher/components/CustomTable";
import CustomDataTable from "../../matcher/components/CustomDataTable";
import KeywordSearch from "../../matcher/components/KeywordSearch";
import { ProcessedDataResponse } from "../../../../graphql/types/graphql";

interface ProcessedViewsData {
  queryProcessedViewsData: ProcessedDataResponse;
}

interface Props {
  tableName: string;
  pageSize: number;
  setPageSize: (value: number) => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  data: ProcessedViewsData;
  loading: boolean;
  identifier: string | null;
}

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableRow {
  FX_UNIQUE_ID: string;
  [key: string]: unknown;
}

interface TableData {
  columns: TableColumn[];
  rows: TableRow[];
}

interface EdgeData<T> {
  edges: T[];
}

function transformToTable<T extends Record<string, unknown>>(data: EdgeData<T> | undefined): TableData {
  if (!data?.edges || data.edges.length === 0) {
    return { columns: [], rows: [] };
  }

  const columns = Object.keys(data.edges[0]).map((key) => ({
    Header: key,
    accessor: key,
  }));

  const rows = data.edges.map((edge) => ({
    ...edge,
    FX_UNIQUE_ID: edge.FX_UNIQUE_ID ?? "",
  })) as TableRow[];

  return { columns, rows };
}

function MyTable(props: Props) {
  const { tableName, pageSize, setPageSize, currentPage, setCurrentPage, data, loading, identifier } = props;
  const { isLoading } = useContext(context);
  const [isUnmatchMode, setIsUnmatchMode] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");

  const {
    selectedRows,
    tableData,
    allColumns,
    loading: unmatchLoading,
    error,
    unmatchLoading: unmatchProcessing,
    totalCount,
    page,
    rowsPerPage,
    handleSelectRow,
    handleSelectAll,
    handleUnmatch,
    handleChangePage,
    handleChangeRowsPerPage,
    setTableData,
    setTotalCount,
    setSelectedRows,
  } = useManualUnmatch(tableName, identifier || undefined);

  const mainTableData = useMemo(() => transformToTable(data.queryProcessedViewsData), [data]);

  const [searchTableData, setSearchTableData] = useState<TableRow[]>([]);

  const {
    matcherTableName,
    fetchedIdentifier,
    mainPageSize,
    mainCurrentPage,
    matcherPageSize,
    matcherCurrentPage,
    selectedLeftRowFXID,
    selectedRightRow,
    filteredMainTableData,
    unmatchedTotalCount,
    matcherTableTransformed,
    matcherTableLoading,
    matcherDataLoading,
    matcherTableTotalCount,
    mergeLoading,
    handleSelectLeftRow,
    handleSelectRightRow,
    handleMerge,
    setMainPageSize,
    setMainCurrentPage,
    setMatcherPageSize,
    setMatcherCurrentPage,
  } = useManualMerge({
    tableName,
    identifier: identifier || "",
    pageSize,
    currentPage,
    mainTableData,
    setTableData,
    setTotalCount,
    setSelectedRows,
    searchTableData,
    hasSearched,
  });

  const [searchTotalCount, setSearchTotalCount] = useState(matcherTableTotalCount);

  const toggleUnmatchMode = useCallback(() => {
    setIsUnmatchMode((prev) => !prev);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setSelectedColumn("");
    setHasSearched(false);
    setSearchTableData(matcherTableTransformed.rows);
    setSearchTotalCount(matcherTableTotalCount);
    setMatcherCurrentPage(0);
  }, [matcherTableTransformed, matcherTableTotalCount, setMatcherCurrentPage]);

  if (loading) return <CircularProgress />;

  return (
    <Container>
      {!isUnmatchMode && (
        <Tooltip title="Edit Processed Data" arrow>
          <IconButton
            onClick={toggleUnmatchMode}
            sx={{
              position: "absolute",
              top: -30,
              right: 0,
              color: colors.secondary.main,
              "&:hover": { color: colors.primary.main },
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}

      {isUnmatchMode ? (
        <>
          <Row justifyContent="space-between" sx={{ alignItems: "center", justifyContent: "center" }}>
            <MDTypography mb="1rem" variant="h4">
              Manual Unmatch
            </MDTypography>
          </Row>
          <StyledCard>
            <ManualUnmatch
              tableData={tableData}
              selectedRows={selectedRows}
              allColumns={allColumns}
              loading={unmatchLoading}
              error={error}
              unmatchLoading={unmatchProcessing}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              identifier={identifier}
              handleSelectRow={handleSelectRow}
              handleSelectAll={handleSelectAll}
              handleUnmatch={handleUnmatch}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </StyledCard>
          <Row justifyContent="space-between" sx={{ alignItems: "center", justifyContent: "center" }}>
            <MDTypography mb="1rem" mt="2rem" variant="h4">
              Manual Merge
            </MDTypography>
          </Row>
          <TablesContainer>
            <TableWrapper>
              <StyledCard>
                <CustomTable
                  total={unmatchedTotalCount}
                  loading={matcherDataLoading}
                  table={filteredMainTableData}
                  pageSize={mainPageSize}
                  currentPage={mainCurrentPage}
                  setPage={setMainCurrentPage}
                  setPageSizeData={setMainPageSize}
                  selectedRowId={selectedLeftRowFXID}
                  onRowSelect={handleSelectLeftRow}
                  identifierColumn="FX_UNIQUE_ID"
                />
              </StyledCard>
            </TableWrapper>
            <MDBox sx={{ display: "flex", alignItems: "center", mx: 2 }}>
              <MDButton
                variant="gradient"
                color="success"
                disabled={!selectedLeftRowFXID || !selectedRightRow || mergeLoading}
                onClick={handleMerge}
                sx={{ boxShadow: 3, borderRadius: "8px" }}
              >
                {mergeLoading ? "Merging..." : "Merge"}
              </MDButton>
            </MDBox>
            <TableWrapper>
              <StyledCard>
                <KeywordSearch
                  tableName={matcherTableName}
                  tableData={matcherTableTransformed}
                  pageSize={matcherPageSize}
                  currentPage={matcherCurrentPage}
                  setCurrentPage={setMatcherCurrentPage}
                  setTableData={setSearchTableData}
                  setTotalCount={setSearchTotalCount}
                  setHasSearched={setHasSearched}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedColumn={selectedColumn}
                  setSelectedColumn={setSelectedColumn}
                  resetSearch={resetSearch}
                />
                {matcherTableName && matcherTableTotalCount === 0 && !hasSearched ? (
                  <MDTypography
                    sx={{
                      fontWeight: "600",
                      color: colors.text.main,
                      fontSize: "1.25rem",
                      padding: "1.5rem",
                      opacity: matcherDataLoading ? 0 : 1,
                    }}
                  >
                    {matcherTableName} has no data!
                  </MDTypography>
                ) : (
                  <CustomTable
                    total={hasSearched ? searchTotalCount : matcherTableTotalCount}
                    loading={matcherDataLoading}
                    table={
                      hasSearched
                        ? { columns: matcherTableTransformed.columns, rows: searchTableData }
                        : matcherTableTransformed
                    }
                    pageSize={matcherPageSize}
                    currentPage={matcherCurrentPage}
                    setPage={setMatcherCurrentPage}
                    setPageSizeData={setMatcherPageSize}
                    selectedRowId={selectedRightRow}
                    onRowSelect={handleSelectRightRow}
                    identifierColumn={fetchedIdentifier}
                  />
                )}
              </StyledCard>
            </TableWrapper>
          </TablesContainer>
        </>
      ) : (
        <StyledCard>
          {data.queryProcessedViewsData.totalCount === 0 ? (
            <MDTypography
              sx={{
                fontWeight: "600",
                color: colors.text.main,
                fontSize: "1.25rem",
                padding: "1.5rem",
                opacity: isLoading ? 0 : 1,
              }}
            >
              {tableName} has no data!
            </MDTypography>
          ) : (
            <CustomDataTable
              total={data.queryProcessedViewsData.totalCount}
              loading={loading}
              table={mainTableData}
              pageSize={pageSize}
              currentPage={currentPage}
              setPage={setCurrentPage}
              setPageSize={setPageSize}
            />
          )}
        </StyledCard>
      )}
    </Container>
  );
}

const Container = styled("div")`
  position: relative;
  padding: 1rem;
`;

const Row = styled(Stack)`
  flex-direction: row;
  gap: 1rem;
`;

const TablesContainer = styled(Stack)`
  flex-direction: row;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
  align-items: flex-start;
`;

const TableWrapper = styled("div")`
  flex: 1;
  min-width: 0;
`;

const MDBox = styled("div")`
  display: flex;
`;

const StyledCard = styled(Card)`
  background: ${colors.background.default};
  box-shadow: ${({ theme }) => theme.shadows[3]};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

export default MyTable;
